import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { UserContext } from '@/components/App';
import * as ROUTES from '@/constants/routes';
import Snackbar from '@/components/Snackbar';
import DrillInPage from '@/components/DrillInPage';
import CorpAdminStore, { CorpAdminContext } from '@/components/CorporateUsers/store';
import { initialsFor } from '@/components/CorporateUsers/shippers/utils';
import {
    AccountBanner,
    BannerAvatar,
    BannerEyebrow,
    BannerLeft,
    BannerMeta,
    BannerName,
    PageSurface,
} from '@/components/CorporateShipper/blocks';
import CompanyInfo from '@/components/Account/CarrierAccount/CompanyInfo';
import Integrations from '@/components/Account/CarrierAccount/Integrations';
import InsuranceContracts from '@/components/Account/CarrierAccount/InsuranceContracts';
import CoverageCapacity from '@/components/Account/CarrierAccount/CoverageCapacity';
import TariffLinks from '@/components/Account/CarrierAccount/TariffLinks';
import ContractedCarriers from '@/components/Account/CarrierAccount/ContractedCarriers';
import { eyebrowFor, tabsFor } from './utils';
import { useCarrierScope } from './hooks';
import CarrierGate from './CarrierGate';
import CarrierTabs from './CarrierTabs';

const TAB_COMPONENTS = {
    'company-info': CompanyInfo,
    integrations: Integrations,
    'insurance-contracts': InsuranceContracts,
    'coverage-capacity': CoverageCapacity,
    'tariff-links': TariffLinks,
    'contracted-carriers': ContractedCarriers,
};

// Only the admin's carrier page takes compliance decisions; the carrier's own account page shows the same tab without.
// Tariff Links needs the relationship for a carrier the Carriers tab doesn't list.
const tabPropsFor = (value, relationship) => {
    if (value === 'insurance-contracts') return { canReview: true };
    if (value === 'tariff-links') return { relationship };
    return {};
};

// The tabs post their notices to CorpAdminContext; one Snackbar shows them, as on the carrier's account page.
const CorpAdminSnackbar = () => {
    const { state, callbacks } = useContext(CorpAdminContext);

    return (
        <Snackbar
            open={!!state.notification.message}
            {...state.notification}
            handleClose={() => callbacks.setNotification({})}
            autohide={4000}
        />
    );
};

const CorporateCarrier = () => {
    const { client_id } = useParams();

    // Everything outside the tab content reads the admin's context.
    const { organizationId, relationship, isPermitted, carrier, loading, error, carrierContext } =
        useCarrierScope(client_id);

    if (!isPermitted || loading || !carrier) {
        return <CarrierGate isPermitted={isPermitted} loading={loading} error={error} carrier={carrier} />;
    }

    const tabs = tabsFor(relationship).map((tab) => {
        const Tab = TAB_COMPONENTS[tab.value];

        return {
            ...tab,
            content: (
                <UserContext.Provider value={carrierContext}>
                    <CorpAdminStore organizationId={organizationId}>
                        <CorpAdminSnackbar />
                        <Tab {...tabPropsFor(tab.value, relationship)} />
                    </CorpAdminStore>
                </UserContext.Provider>
            ),
        };
    });

    return (
        <DrillInPage
            backLabel="Accounts"
            fallbackPath={ROUTES.CORPORATE_USERS}
            Surface={PageSurface}
            Tabs={CarrierTabs}
            tabs={tabs}
            header={
                <AccountBanner>
                    <BannerLeft>
                        <BannerAvatar>{initialsFor(carrier.business_name)}</BannerAvatar>
                        <div>
                            <BannerEyebrow>{eyebrowFor(relationship)}</BannerEyebrow>
                            <BannerName>{carrier.business_name || 'Unnamed carrier'}</BannerName>
                            <BannerMeta>Client ID: {carrier.client_id}</BannerMeta>
                        </div>
                    </BannerLeft>
                </AccountBanner>
            }
        />
    );
};

export default CorporateCarrier;
